import { upperFirst } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';

import { daysUntil, formatClock12 } from '../../helpers';
import { Badge } from '../Badge';
import TaskRow from '../TaskRow';
import TaskTableHeader from '../TaskTableHeader';
import { ReadinessSquares, SmallCaps, STATUS_WORD, TopicBand, zoneAbbr } from '../Visit';
import styles from './MemberRecord.module.scss';

const READINESS_BADGE = { green: 'green', yellow: 'yellow', red: 'red', neutral: 'outline' };

const DateGutter = ({ date, accent }) => {
  const parsed = moment(date, 'YYYY-MM-DD');
  const showYear = parsed.year() !== moment().year();
  return (
    <div className={styles.dateGutter}>
      <div className={`${styles.dateMonth} ${accent ? styles.dateAccent : ''}`}>
        {parsed.format('MMM').toUpperCase()}
      </div>
      <div className={`${styles.dateDay} ${accent ? styles.dateAccent : ''}`}>{parsed.date()}</div>
      <div className={styles.dateYear}>{showYear ? parsed.year() : ''}</div>
    </div>
  );
};

const Node = ({ kind, status }) => {
  if (kind === 'visit') return <span className={`${styles.node} ${styles.nodeVisit}`} />;
  if (kind === 'upcoming') {
    return (
      <span
        className={`${styles.node} ${styles.nodeUpcoming} ${styles[`node${upperFirst(status)}`]}`}
      />
    );
  }
  if (kind === 'future') return <span className={`${styles.node} ${styles.nodeFuture}`} />;
  return <span className={`${styles.node} ${styles.nodeDot}`} />;
};

const AnchoredRow = ({ episode, accent, status, isLast, children }) => (
  <div className={styles.anchoredRow}>
    <DateGutter date={episode.date} accent={accent} />
    <div className={styles.spine}>
      <span className={styles.spineLead} />
      <Node kind={episode.kind} status={status} />
      {!isLast && <span className={styles.spineTail} />}
    </div>
    <div className={styles.rowBody}>{children}</div>
  </div>
);

const TaskRows = ({ tasks, onOpenTask, onAssignToMe, assigningIds, compact }) => (
  <>
    {!compact && <TaskTableHeader />}
    {tasks.map((task) => (
      <TaskRow
        key={task.id}
        task={task}
        compact={compact}
        onSelect={() => onOpenTask(task)}
        onAssignToMe={onAssignToMe}
        isAssigning={assigningIds.has(task.id)}
      />
    ))}
  </>
);

const visitMeta = (episode) => [episode.clinician, episode.location].filter(Boolean).join(' · ');

const VisitEpisode = ({ episode, showCompleted, onOpenTask, onAssignToMe, assigningIds }) => (
  <div className={styles.episodeCard}>
    <div
      className={`${styles.episodeHead} ${
        showCompleted && episode.tasks.length > 0 ? styles.episodeHeadRuled : ''
      }`}
    >
      <span className={styles.episodeTitle}>{episode.label}</span>
      <span className={styles.flex} />
      <span className={styles.episodeMeta}>{visitMeta(episode)}</span>
    </div>
    {showCompleted && episode.tasks.length > 0 && (
      <>
        <TopicBand
          density="group"
          label="Pre-visit work"
          meta={`${episode.tasks.length} completed`}
          className={styles.groupBand}
        />
        <TaskRows
          tasks={episode.tasks}
          onOpenTask={onOpenTask}
          onAssignToMe={onAssignToMe}
          assigningIds={assigningIds}
          compact
        />
      </>
    )}
  </div>
);

const BetweenEpisode = ({ episode, showCompleted, onOpenTask, onAssignToMe, assigningIds }) => {
  if (!episode.open && !showCompleted) {
    return (
      <div className={styles.collapsedCluster}>
        {episode.label} · {episode.tasks.length} tasks completed
      </div>
    );
  }
  return (
    <div className={`${styles.cluster} ${episode.open ? styles.clusterOpen : ''}`}>
      <TopicBand headingLevel="h3" label={episode.label}>
        {episode.open ? (
          <span className={styles.bandRight}>
            <span className={styles.bandNow}>Now</span>
            <Badge variant="red">{episode.tasks.length} open</Badge>
          </span>
        ) : (
          <span className={styles.bandRight}>
            <span className={styles.bandNow}>Completed</span>
            <span className={styles.allCompleteText}>All complete</span>
          </span>
        )}
      </TopicBand>
      <TaskRows
        tasks={episode.tasks}
        onOpenTask={onOpenTask}
        onAssignToMe={onAssignToMe}
        assigningIds={assigningIds}
        compact={!episode.open}
      />
    </div>
  );
};

const UpcomingEpisode = ({ episode, onOpenVisit }) => {
  const days = daysUntil(episode.date);
  const zone = zoneAbbr(episode.timeZone, episode.date, episode.time);
  const status = episode.readinessStatus;
  return (
    <div className={`${styles.upcoming} ${styles[`upcoming${upperFirst(status)}`]}`}>
      <div className={styles.upcomingHead}>
        <span className={styles.episodeTitle}>{episode.label}</span>
        <Badge variant={READINESS_BADGE[status]}>{STATUS_WORD.readiness[status]}</Badge>
        <span className={styles.flex} />
        <span className={styles.upcomingWhen}>
          {days === 0 ? 'Today' : `in ${days}d`}
          {episode.time
            ? ` · ${[formatClock12(episode.time), zone].filter(Boolean).join(' ')}`
            : ''}
        </span>
      </div>
      <div className={styles.episodeMeta}>{visitMeta(episode)}</div>
      {episode.readinessTotal > 0 && (
        <div className={styles.upcomingReadiness}>
          <SmallCaps as="div">Readiness</SmallCaps>
          <ReadinessSquares
            blocks={episode.readinessBlocks}
            done={episode.readinessDone}
            total={episode.readinessTotal}
          />
        </div>
      )}
      <button type="button" className={styles.lineButton} onClick={() => onOpenVisit(episode)}>
        Open in Prepare
      </button>
    </div>
  );
};

const FutureEpisode = ({ episode }) => (
  <div className={styles.future}>
    <span className={styles.futureTitle}>{episode.label}</span>
    <Badge variant="outline" className={styles.projected}>
      BOOKED
    </Badge>
  </div>
);

const NowMarker = ({ today }) => (
  <div className={styles.nowRow}>
    <span className={styles.nowLabel}>Today</span>
    <span className={styles.nowDot} />
    <span className={styles.nowLine} />
    <span className={styles.nowDate}>{moment(today).format('MMM D, YYYY')}</span>
  </div>
);

const MemberTimeline = ({
  model,
  showCompleted,
  onOpenTask,
  onOpenVisit,
  onAssignToMe,
  assigningIds,
}) => {
  const list = [...model.episodes].reverse();

  return (
    <div className={styles.timelineCard}>
      {list.map((episode, index) => {
        const isLast = index === list.length - 1;
        if (episode.kind === 'now') return <NowMarker key={episode.id} today={episode.date} />;
        if (episode.kind === 'between') {
          return (
            <div key={episode.id} className={styles.anchoredRow}>
              <span className={styles.dateGutter} />
              <span className={styles.spine}>
                <span className={styles.spineFull} />
              </span>
              <div className={styles.rowBody}>
                <BetweenEpisode
                  episode={episode}
                  showCompleted={showCompleted}
                  onOpenTask={onOpenTask}
                  onAssignToMe={onAssignToMe}
                  assigningIds={assigningIds}
                />
              </div>
            </div>
          );
        }
        if (episode.kind === 'future') {
          return (
            <AnchoredRow key={episode.id} episode={episode} isLast={isLast}>
              <FutureEpisode episode={episode} />
            </AnchoredRow>
          );
        }
        if (episode.kind === 'upcoming') {
          return (
            <AnchoredRow
              key={episode.id}
              episode={episode}
              accent
              status={episode.readinessStatus}
              isLast={isLast}
            >
              <UpcomingEpisode episode={episode} onOpenVisit={onOpenVisit} />
            </AnchoredRow>
          );
        }
        return (
          <AnchoredRow
            key={episode.id}
            episode={episode}
            accent={daysUntil(episode.date) > -30}
            isLast={isLast}
          >
            <VisitEpisode
              episode={episode}
              showCompleted={showCompleted}
              onOpenTask={onOpenTask}
              onAssignToMe={onAssignToMe}
              assigningIds={assigningIds}
            />
          </AnchoredRow>
        );
      })}
    </div>
  );
};

DateGutter.propTypes = { date: PropTypes.string.isRequired, accent: PropTypes.bool };
Node.propTypes = { kind: PropTypes.string.isRequired, status: PropTypes.string };
AnchoredRow.propTypes = {
  episode: PropTypes.object.isRequired,
  accent: PropTypes.bool,
  status: PropTypes.string,
  isLast: PropTypes.bool,
  children: PropTypes.node,
};
TaskRows.propTypes = {
  tasks: PropTypes.array.isRequired,
  onOpenTask: PropTypes.func.isRequired,
  onAssignToMe: PropTypes.func.isRequired,
  assigningIds: PropTypes.instanceOf(Set).isRequired,
  compact: PropTypes.bool,
};
VisitEpisode.propTypes = {
  episode: PropTypes.object.isRequired,
  showCompleted: PropTypes.bool,
  onOpenTask: PropTypes.func.isRequired,
  onAssignToMe: PropTypes.func.isRequired,
  assigningIds: PropTypes.instanceOf(Set).isRequired,
};
BetweenEpisode.propTypes = VisitEpisode.propTypes;
UpcomingEpisode.propTypes = {
  episode: PropTypes.object.isRequired,
  onOpenVisit: PropTypes.func.isRequired,
};
FutureEpisode.propTypes = { episode: PropTypes.object.isRequired };
NowMarker.propTypes = { today: PropTypes.string.isRequired };
MemberTimeline.propTypes = {
  model: PropTypes.object.isRequired,
  showCompleted: PropTypes.bool,
  onOpenTask: PropTypes.func.isRequired,
  onOpenVisit: PropTypes.func.isRequired,
  onAssignToMe: PropTypes.func.isRequired,
  assigningIds: PropTypes.instanceOf(Set).isRequired,
};

export default MemberTimeline;
